import { StyledComponent } from '@emotion/styled';
import { MenuList, Popper, SlotProps } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/system';
import React from 'react';

import { Clickable } from '@/components/atoms/Clickable';

interface Props<ItemType> {
  readonly defaultValue?: string;
  readonly value?: string;
  readonly fullWidth?: boolean;
  readonly results?: readonly ItemType[];
  readonly name?: string;
  readonly label?: string;

  readonly itemComponent: React.FC<{ readonly item: ItemType }>;
  readonly endAdornment?: React.ReactElement;

  onItemClick?(item: ItemType): void;
  onBlur?(event: React.FocusEvent): void;
  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
}

// Generic component with forwardRef
export const InputWithSearchDropdown = React.forwardRef(function InputWithSearchDropdown<ItemType>(
  props: Props<ItemType>,
  ref: React.Ref<HTMLInputElement>,
): React.ReactElement {
  const {
    defaultValue,
    value,
    fullWidth,
    results = [],
    itemComponent: ItemComponent,
    endAdornment,
    name,
    label,
    onItemClick,
    onBlur,
    onChange,
  } = props;

  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  React.useEffect((): VoidFunction => {
    setIsOpen(results.length > 0 && !!anchorEl);

    return (): void => {
      setIsOpen(false);
    };
  }, [anchorEl, results]);

  const StyledTextField = React.useMemo(
    (): StyledComponent<any> =>
      styled(TextField)({
        '& .MuiOutlinedInput-root': {
          borderRadius: 24,
          padding: 0,
        },
      }),
    [],
  );

  const onItemClickWrapper = React.useCallback(
    (item: ItemType): void => {
      onItemClick?.(item);
      setIsOpen(false);
    },
    [onItemClick],
  );

  const slotProps = React.useMemo(
    (): SlotProps<any, any, any> => ({
      input: {
        ref: ref,
        endAdornment: endAdornment,
      },
    }),
    [endAdornment, ref],
  );

  const popperStyle = React.useMemo(
    (): React.CSSProperties => ({ width: anchorEl?.clientWidth }),
    [anchorEl?.clientWidth],
  );

  const onClickHandler = React.useCallback((event: React.MouseEvent): void => {
    setAnchorEl(event.currentTarget as HTMLDivElement);
  }, []);

  return (
    <Box width="100%">
      <StyledTextField
        defaultValue={defaultValue}
        value={value}
        fullWidth={fullWidth}
        name={name}
        label={label}
        slotProps={slotProps}
        onBlur={onBlur}
        onChange={onChange}
        onClick={onClickHandler}
      />
      <Popper open={isOpen} anchorEl={anchorEl} placement="bottom-start" style={popperStyle}>
        <Paper elevation={1}>
          <MenuList>
            {results.map(
              (item: ItemType, index: number): React.ReactElement => (
                <Clickable key={index} data={item} onClick={onItemClickWrapper}>
                  <MenuItem>
                    <ItemComponent item={item} />
                  </MenuItem>
                </Clickable>
              ),
            )}
          </MenuList>
        </Paper>
      </Popper>
    </Box>
  );
}) as <ItemType>(
  props: Props<ItemType> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement;
